import * as React from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { UserToken } from '../../../../../amorg-common';
import { ProtectedPageContext } from '../../../../_contexts/ProtectedPageContext';
import { tablet } from '../../../../../designSystem/BreakPoints';
import { Flex } from '../../../../../designSystem/compositeComponents/FlexHelpers';
import { Divider } from '../../../../../designSystem/Divider';
import { Icon } from '../../../../../designSystem/icons/Icons';
import { Strong } from '../../../../../designSystem/typography/Body';
import { MenuItem, MenuItemGroup, MenuItemGroupProps, MenuItemProps } from './MenuItem';

type MenuItemDefinition = 
    MenuItemProps & {
        type:'menu-item',
        show:boolean
    };

type MenuItemGroupDefinition =
    MenuItemGroupProps & {
        type:'menu-item-group',
        subItems: MenuItemDefinition[]
    };

type MenuItemDividerDefinition = {
    type:'menu-divider'
}

type NavigationItemDefinition = MenuItemDefinition | MenuItemGroupDefinition | MenuItemDividerDefinition;

type LeftMenuNavigationProps = {
    activeUser: UserToken | null,
    selectedMenuItemKey?: string,
    items: NavigationItemDefinition[],
    isMobileNavigationOpen: boolean,
    onCloseMobileNavigation: () => void,
};

export const LeftMenuNavigation = ({ activeUser, selectedMenuItemKey, items, isMobileNavigationOpen, onCloseMobileNavigation }: LeftMenuNavigationProps): JSX.Element => 
{
    const context = React.useContext(ProtectedPageContext);

    useEffect(() => {
        if (!isMobileNavigationOpen)
            return;

        document.body.classList.add('no-scroll');

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape')
                onCloseMobileNavigation();
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.classList.remove('no-scroll');
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMobileNavigationOpen, onCloseMobileNavigation]);

    const applyPartnerLogo = () =>
    {

        if (context.user?.partner === 'Crecea')
        {
            return <img src="/images/partner/crecea/crecea_logo_white.svg" />;
        }

        if(context.user?.partner === 'Joblife')
        {
            return <img width="200" src="/images/partner/creceajoblife/CreceaJoblife_white.svg" />;
        }

    };

    return (
        <>
        <Backdrop isMobileNavigationOpen={isMobileNavigationOpen} onClick={onCloseMobileNavigation} />
        <OuterWrapper isMobileNavigationOpen={isMobileNavigationOpen}>

            <div>
                <CloseButtonRow>
                    <CloseButton type="button" aria-label="Luk menu" onClick={onCloseMobileNavigation}>
                        <Icon name="actions_close" size="small" color="white" />
                    </CloseButton>
                </CloseButtonRow>
                <div>
                        <div style={{margin:'0px 16px'}}>{applyPartnerLogo()}</div>
                        <Navigation className="_navigation">
                            <List>
                                <>
                                    {activeUser &&
                                        <>
                                            {items.map((item, index) => {
                                                if(item.type === 'menu-item')
                                                {
                                                    if(item.show)
                                                    {
                                                        return <MenuItem key={index} selectedMenuItemKey={selectedMenuItemKey} {...item} />
                                                    }
                                                    else {
                                                        return null;
                                                    }
                                                }
                                                else if(item.type === 'menu-item-group')
                                                {
                                                    if(item.subItems.some(subItem => subItem.show))
                                                    {
                                                        return (
                                                            <MenuItemGroup key={index} {...item}>
                                                                {item.subItems.map((subItem, subIndex) => {
                                                                    if(subItem.type === 'menu-item' && subItem.show)
                                                                    {
                                                                        return <MenuItem key={subIndex} selectedMenuItemKey={selectedMenuItemKey} {...subItem} />
                                                                    }
                                                                    return null;
                                                                })}
                                                            </MenuItemGroup>
                                                        )
                                                    }
                                                }
                                                else if(item.type === 'menu-divider')
                                                {
                                                    const hasItemsBefore = index > 0 
                                                        && items.slice(0, index).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    const hasItemsAfter = index < items.length - 1 
                                                        && items.slice(index + 1).some(i => 
                                                            (i.type === 'menu-item' && i.show) || (i.type === 'menu-item-group' && i.subItems.some(subItem => subItem.show)));

                                                    if(hasItemsBefore && hasItemsAfter)
                                                    {
                                                        return <Divider key={index} color={context.colors.mainNavigation.divider} />
                                                    }
                                                }
                                            })}
                                        </>
                                    }
                                </>
                            </List>
                        </Navigation>
                    </div>
            </div>
            <div>
                <Flex flexDirection="row" alignItems='center' marginTop={8}>
                    {(context.user?.partner) &&
                        <PoweredBy>Powered by</PoweredBy>
                    }
                    <LogoContainer>
                        <img src="/images/amorg_logo_blue.svg" />
                    </LogoContainer>
                    <Strong>AMORG</Strong>
                </Flex>
            </div>
        </OuterWrapper>
        </>
    );
};



const Backdrop = styled.div<{ isMobileNavigationOpen: boolean }>`
    display: none;

    @media (max-width: ${tablet}px) {
        display: ${props => props.isMobileNavigationOpen ? 'block' : 'none'};
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.4);
        z-index: 999;
    }
`;

const CloseButtonRow = styled.div`
    display: none;

    @media (max-width: ${tablet}px) {
        display: flex;
        justify-content: flex-end;
        padding: 0 8px;
    }
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
`;

const OuterWrapper = styled.div<{ isMobileNavigationOpen: boolean; }>`  
    grid-area: navigation;
    font-family: "Noto Sans", sans-serif;
    color: white;
    background-color: ${props => props.theme.colors.mainNavigation.background};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    overflow-y: auto;
    
    /* Desktop styles */
    width: 320px;
    height: 100%;
    padding: 24px 0;
    position: fixed;

    @media (max-width: ${tablet}px) {
        /* Mobile styles */
        top: 0;
        left: 0;
        width: 300px;
        max-width: 85vw;
        height: 100vh;
        height: 100dvh;
        padding: 8px 0 16px;
        z-index: 1000;
        transform: translateX(${props => props.isMobileNavigationOpen ? '0' : '-100%'});
        visibility: ${props => props.isMobileNavigationOpen ? 'visible' : 'hidden'};
        transition: transform 0.25s ease, visibility 0.25s;
    }
`;


const LogoContainer = styled.div`
    height: 32px;
    width: 32px;
    margin-left: 8px;
    margin-right: 8px;
`;

const Navigation = styled.nav`
    margin:12px auto;   
`;

const List = styled.ul`
    list-style: none;
    margin:0px;
    padding:0px;
`;

const PoweredBy = styled.div`
    font-size: 12px;
    color: white;
    font-weight: 700;
`;